<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { RotateCcw, Search } from '@lucide/vue'
import { Badge, Button, EmptyState, Input, Modal, Pagination, Section, Select, Spinner, Table, Toolbar } from '@drama/ui'

import { getMediaAssetFacets, listMediaAssets } from '../../api/asset-library.js'
import { formatBytes, formatDate } from '../intent-video/format.js'

const { t } = useI18n()
const router = useRouter()

const GLOBAL_VALUE = '__global__'
const ALL_VALUE = ''

const loading = ref(false)
const error = ref('')
const items = ref([])
const total = ref(0)
const page = ref(1)
const pages = ref(1)
const pageSize = ref(50)
/** type 默认锁定 video——5046 行里大头是 TTS 音频，全量摊开没法看。 */
const filters = reactive({ type: 'video', pack: ALL_VALUE, q: '' })

/** 只有这四种在 assetLibrary.type.* 里有译名；vg_media_assets 里历史上还写过 npc/reference/asset/ui/scene/player 这些，原样显示，不硬翻译。 */
const KNOWN_TYPES = ['image', 'video', 'audio', 'prompt']
function typeLabel(type) {
  return KNOWN_TYPES.includes(type) ? t(`visualGameAdmin.assetLibrary.type.${type}`) : type
}

const types = ref([])
const packs = ref([])

const typeOptions = computed(() => types.value.map(type => ({ value: type, label: typeLabel(type) })))

const packOptions = computed(() => [
  { value: ALL_VALUE, label: t('visualGameAdmin.assetLibrary.allMedia.allPacks') },
  { value: GLOBAL_VALUE, label: t('visualGameAdmin.assetLibrary.allMedia.global') },
  ...packs.value.map(pack => ({ value: pack.pack_id, label: pack.pack_title })),
])

/** 六列够看清是什么、大不大、最近动没动过——其余字段（id/object_key/mime/hash/...）点进详情页看全的。 */
const columns = computed(() => [
  { key: 'pack', label: t('visualGameAdmin.assetLibrary.allMedia.pack'), width: '160px' },
  { key: 'type', label: t('visualGameAdmin.assetLibrary.table.type'), width: '90px' },
  { key: 'preview', label: t('visualGameAdmin.assetLibrary.allMedia.preview'), width: '260px' },
  { key: 'size', label: t('visualGameAdmin.assetLibrary.allMedia.size'), width: '190px' },
  { key: 'metadata', label: t('visualGameAdmin.assetLibrary.allMedia.metadata'), width: '220px', mono: true },
  { key: 'updated_at', label: t('visualGameAdmin.assetLibrary.allMedia.updatedAt'), width: '170px' },
])

const summary = computed(() => t('visualGameAdmin.assetLibrary.pager.summary', { total: total.value, page: page.value, pages: pages.value }))

onMounted(async () => {
  try {
    const facets = await getMediaAssetFacets()
    types.value = facets.types
    packs.value = facets.packs
  } catch {
    // 下拉取不到就退化成只有当前默认值可选，不挡主列表加载。
    types.value = [filters.type]
  }
  load()
})

async function load() {
  loading.value = true
  error.value = ''
  try {
    const data = await listMediaAssets({
      type: filters.type || undefined,
      pack_id: filters.pack && filters.pack !== GLOBAL_VALUE ? filters.pack : undefined,
      global: filters.pack === GLOBAL_VALUE ? '1' : undefined,
      q: filters.q.trim() || undefined,
      page: page.value,
      limit: pageSize.value,
    })
    items.value = data.items
    total.value = data.total
    pages.value = data.pages
    page.value = data.page
  } catch (requestError) {
    error.value = requestError.message
  } finally {
    loading.value = false
  }
}

function applyFilters() {
  page.value = 1
  load()
}

function resetFilters() {
  filters.type = 'video'
  filters.pack = ALL_VALUE
  filters.q = ''
  applyFilters()
}

function changePageSize(size) {
  pageSize.value = size
  applyFilters()
}

function prevPage() {
  page.value -= 1
  load()
}

function nextPage() {
  page.value += 1
  load()
}

/** 预览方式按 mime 判断，mime 缺失（老数据）再退回 type——两者都判不出就只给个链接。 */
function previewKind(row) {
  const probe = row.mime || row.type || ''
  if (probe.startsWith('video/') || row.type === 'video') return 'video'
  if (probe.startsWith('audio/') || row.type === 'audio') return 'audio'
  if (probe.startsWith('image/') || row.type === 'image') return 'image'
  return row.public_url ? 'link' : 'none'
}

/** OSS 对象大多带 Content-Disposition: attachment，<a target="_blank"> 点了直接下载，弹窗看图才是预览。 */
const imagePreviewOpen = ref(false)
const imagePreviewUrl = ref('')
function openImagePreview(url) {
  imagePreviewUrl.value = url
  imagePreviewOpen.value = true
}

function metadataText(metadata) {
  return metadata ? JSON.stringify(metadata) : '-'
}

function openDetail(row) {
  router.push({ name: 'media-asset-detail', params: { id: row.id } })
}
</script>

<template>
  <div class="amt" data-insp-path="apps/visual-game-admin/src/pages/asset-library/AllMediaTab.vue">
    <Section>
      <Toolbar>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.assetLibrary.table.type') }}</label>
          <Select v-model="filters.type" :options="typeOptions" size="sm" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.assetLibrary.allMedia.pack') }}</label>
          <Select v-model="filters.pack" :options="packOptions" size="sm" />
        </div>
        <div class="drama-toolbar-field drama-toolbar-field--wide">
          <label>{{ t('visualGameAdmin.assetLibrary.filters.search') }}</label>
          <Input
            v-model="filters.q"
            type="search"
            size="sm"
            :placeholder="t('visualGameAdmin.assetLibrary.allMedia.searchPlaceholder')"
            @keyup.enter="applyFilters"
          />
        </div>
        <template #actions>
          <Button size="sm" :loading="loading" @click="applyFilters">
            <Search :size="14" /> {{ t('visualGameAdmin.assetLibrary.filters.apply') }}
          </Button>
          <Button variant="ghost" size="sm" :disabled="loading" @click="resetFilters">
            <RotateCcw :size="14" /> {{ t('visualGameAdmin.common.reset') }}
          </Button>
        </template>
      </Toolbar>
    </Section>

    <div v-if="loading" class="vga-state"><Spinner /><span>{{ t('visualGameAdmin.common.loading') }}</span></div>
    <div v-else-if="error" class="vga-state">
      <p class="vga-error">{{ error }}</p>
      <Button variant="secondary" @click="load">{{ t('visualGameAdmin.common.retry') }}</Button>
    </div>
    <template v-else>
      <Section :padded="false">
        <EmptyState
          v-if="items.length === 0"
          :title="t('visualGameAdmin.assetLibrary.emptyTitle')"
          :description="t('visualGameAdmin.assetLibrary.emptyDescription')"
        />
        <Table v-else :columns="columns" :rows="items" clickable @row-click="openDetail">
          <template #pack="{ row }">
            <span v-if="row.pack_id">{{ row.pack_title }}</span>
            <Badge v-else size="sm">{{ t('visualGameAdmin.assetLibrary.allMedia.global') }}</Badge>
          </template>
          <template #type="{ row }">
            <Badge size="sm">{{ typeLabel(row.type) }}</Badge>
          </template>
          <template #preview="{ row }">
            <!-- @click.stop：点播放/放大不该跳转详情页，跳转由点行的其余区域触发。 -->
            <div class="amt__preview-cell" @click.stop>
              <video v-if="previewKind(row) === 'video'" :src="row.public_url" controls preload="none" class="amt__video" />
              <audio v-else-if="previewKind(row) === 'audio'" :src="row.public_url" controls preload="none" class="amt__audio" />
              <button v-else-if="previewKind(row) === 'image'" type="button" class="amt__thumb-btn" @click="openImagePreview(row.public_url)">
                <img :src="row.public_url" class="amt__thumb" :alt="row.tag" loading="lazy" />
              </button>
              <a v-else-if="previewKind(row) === 'link'" :href="row.public_url" target="_blank" rel="noopener">
                {{ t('visualGameAdmin.assetLibrary.allMedia.open') }}
              </a>
              <span v-else>-</span>
            </div>
          </template>
          <template #size="{ row }">
            <span>{{ formatBytes(row.size) }}</span>
            <span v-if="row.width && row.height" class="amt__dim"> · {{ row.width }}×{{ row.height }}</span>
          </template>
          <template #metadata="{ row }"><span class="amt__clamp" :title="metadataText(row.metadata)">{{ metadataText(row.metadata) }}</span></template>
          <template #updated_at="{ row }">{{ formatDate(row.updated_at ?? row.created_at) }}</template>
        </Table>
      </Section>
      <Pagination
        :has-prev="page > 1"
        :has-next="page < pages"
        :loading="loading"
        :summary="summary"
        :prev-label="t('visualGameAdmin.assetLibrary.pager.prev')"
        :next-label="t('visualGameAdmin.assetLibrary.pager.next')"
        :page-size="pageSize"
        :page-size-options="[20, 50, 100]"
        :page-size-label="t('visualGameAdmin.assetLibrary.pager.pageSize')"
        @prev="prevPage"
        @next="nextPage"
        @update:page-size="changePageSize"
      />
    </template>

    <Modal :open="imagePreviewOpen" width="min(90vw, 960px)" @update:open="imagePreviewOpen = $event">
      <img :src="imagePreviewUrl" class="amt__preview-image" alt="" />
    </Modal>
  </div>
</template>

<style scoped>
.amt__thumb { width: 64px; height: 64px; object-fit: cover; border-radius: var(--drama-radius-sm); border: 1px solid var(--drama-color-border); }
.amt__thumb-btn { padding: 0; border: none; background: none; cursor: zoom-in; line-height: 0; }
.amt__video { width: 220px; max-height: 140px; border-radius: var(--drama-radius-sm); }
.amt__audio { width: 240px; height: 32px; }
.amt__dim { color: var(--drama-color-text-muted); }
.amt__clamp { display: inline-block; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; vertical-align: bottom; }
.amt__preview-image { display: block; max-width: 100%; max-height: 80vh; margin: 0 auto; border-radius: var(--drama-radius-sm); }
</style>
